<template>
  <div class="w-full flex flex-col items-center justify-center">
    <div
      class="w-64 h-48 bg-grey-800 rounded-lg flex flex-col relative overflow-hidden mb-2"
    >
      <div
        class="absolute top-0 left-0 bg-grey-900 negative-slope h-px z-10"
      ></div>
      <div
        class="absolute top-0 left-0 bg-grey-900 positive-slope h-px z-10"
      ></div>
      <div class="w-full h-8 flex items-center justify-center">
        <div
          class="border-grey-700 border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
          :class="{
            'border text-grey-600': !marginSelected('top'),
            'bg-pink text-white': marginSelected('top')
          }"
          @click="select('top', true);"
        >
          {{ getMarginValue('top') }}
        </div>
      </div>
      <div class="w-full flex flex-grow">
        <div class="h-full box-side flex items-center justify-center">
          <div
            class="border-grey-700 border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
            :class="{
              'border text-grey-600': !marginSelected('left'),
              'bg-pink text-white': marginSelected('left')
            }"
            @click="select('left', true);"
          >
            {{ getMarginValue('left') }}
          </div>
        </div>
        <div
          class="h-full flex-grow flex border-8 border-solid border-grey-900 box-inner flex-col relative"
        >
          <div class="w-full h-8 flex items-center justify-center">
            <div
              class="border-grey-700 border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
              :class="{
                'border text-grey-600': !paddingSelected('top'),
                'bg-pink text-white': paddingSelected('top')
              }"
              @click="select('top');"
            >
              {{ getPaddingValue('top') }}
            </div>
          </div>
          <div class="w-full flex-grow flex">
            <div class="h-full box-side flex items-center justify-center">
              <div
                class="border-grey-700 border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
                :class="{
                  'border text-grey-600': !paddingSelected('left'),
                  'bg-pink text-white': paddingSelected('left')
                }"
                @click="select('left');"
              >
                {{ getPaddingValue('left') }}
              </div>
            </div>
            <div class="h-full flex-grow flex bg-grey-900"></div>
            <div class="h-full box-side flex items-center justify-center">
              <div
                class="border-grey-700 border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
                :class="{
                  'border text-grey-600': !paddingSelected('right'),
                  'bg-pink text-white': paddingSelected('right')
                }"
                @click="select('right');"
              >
                {{ getPaddingValue('right') }}
              </div>
            </div>
          </div>
          <div class="w-full h-8 flex items-center justify-center">
            <div
              class="border-grey-700 border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
              :class="{
                'border text-grey-600': !paddingSelected('bottom'),
                'bg-pink text-white': paddingSelected('bottom')
              }"
              @click="select('bottom');"
            >
              {{ getPaddingValue('bottom') }}
            </div>
          </div>
          <div class="absolute bottom-0 left-0 info-sm pl-6 text-grey-300">
            {{ $t('frontend_sites_spacing_style_input_padding') }}
          </div>
        </div>
        <div class="h-full box-side flex items-center justify-center">
          <div
            class="border-grey-700 border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
            :class="{
              'border text-grey-600': !marginSelected('right'),
              'bg-pink text-white': marginSelected('right')
            }"
            @click="select('right', true);"
          >
            {{ getMarginValue('right') }}
          </div>
        </div>
      </div>
      <div class="w-full h-8 flex items-center justify-center">
        <div
          class="border-grey-700 border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
          :class="{
            'border text-grey-600': !marginSelected('bottom'),
            'bg-pink text-white': marginSelected('bottom')
          }"
          @click="select('bottom', true);"
        >
          {{ getMarginValue('bottom') }}
        </div>
      </div>
      <div class="absolute bottom-0 left-0 info-sm pl-6 text-grey-300">
        {{ $t('frontend_sites_spacing_style_input_margin') }}
      </div>
    </div>
    <div v-if="itemSelected()" class="w-full">
      <gd-slider
        :value="sliderValue()"
        min="0"
        :max="max"
        step="1"
        dark
        :format-value="formatValue"
        @change="updateValue"
      />
      <div v-if="marginIsSelected" class="mt-2">
        <gd-checkbox
          v-model="negativeSelected"
          class="float-left"
          @change="updateValue(0);"
        />
        <span class="ml-3 negativeText">{{ $t('frontend_sites_spacing_style_input_make_negative') }}</span>
      </div>
    </div>
  </div>
</template>
<style scoped>
.negativeText {
  color: white;
  font-weight: 700;
  font-size: 14px;
}
.negative-slope {
  width: 32em;
  margin-left: -100%;
  transform: skewY(36.7deg);
}
.box-side {
  width: 2.6rem;
}
.box-inner {
  border-left-width: 11px;
  border-right-width: 11px;
}
.positive-slope {
  width: 32em;
  transform: skewY(143.3deg);
}
</style>
<script>
import GdCheckbox from './Checkbox';
import GdSlider from './Slider';
import { ucfirst } from '../utils';

export default {
  name: 'SpacingPanel',
  components: {
    GdSlider,
    GdCheckbox
  },
  props: ['paddingOptions', 'marginOptions', 'values'],
  data() {
    return {
      selected: null,
      marginIsSelected: false,
      lastSelected: null,
      negativeSelected: false
    };
  },
  computed: {
    max() {
      if (!this.marginIsSelected) return this.paddingOptions.length;
      if (this.negativeSelected) return this.marginOptions.indexOf('0');
      return this.marginOptions.length - this.marginOptions.indexOf('0');
    }
  },
  methods: {
    itemSelected() {
      if (this.selected && this.selected.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    marginSelected(side) {
      if (!this.marginIsSelected) return false;
      if (this.selected && this.selected.includes(side)) {
        return true;
      }
      return false;
    },
    paddingSelected(side) {
      if (this.marginIsSelected) return false;
      if (this.selected && this.selected.includes(side)) {
        return true;
      }
      return false;
    },
    formatValue(value) {
      if (value === 0) return '-';
      let formatted = this.paddingOptions[value - 1] || '-';
      if (this.marginIsSelected && this.negativeSelected) {
        formatted = this.marginOptions[value - 1] || '-';
      } else if (this.marginIsSelected) {
        formatted =
          this.marginOptions[value + this.marginOptions.indexOf('0') - 1] ||
          '-';
      }
      return formatted;
    },
    updateValue(index) {
      index = parseInt(index);
      let prefix = 'padding';
      let options = this.paddingOptions;
      if (this.marginIsSelected) {
        prefix = 'margin';
        options = this.marginOptions;
      }
      let value = null;
      if (index > 0 && (this.negativeSelected || !this.marginIsSelected)) {
        value = options[index - 1];
      } else if (index > 0) {
        value = options[index + this.marginOptions.indexOf('0') - 1];
      }
      this.selected.forEach(side => {
        this.$emit('set-style', { property: prefix + ucfirst(side), value });
      });
      this.$forceUpdate();
    },
    getMarginValue(side) {
      return this.values['margin' + ucfirst(side)] || '-';
    },
    getPaddingValue(side) {
      return this.values['padding' + ucfirst(side)] || '-';
    },
    select(side, isMargin) {
      let removed = false;
      if (
        this.selected &&
        this.selected.includes(side) &&
        !!isMargin === this.marginIsSelected
      ) {
        this.selected = this.selected.filter(entry => {
          if (entry === side) {
            removed = true;
            return false;
          }
          return true;
        });
      }
      if (this.selected === null) {
        this.selected = [];
      }
      if (
        (this.lastSelected === 'margin' && !isMargin) ||
        (this.lastSelected === 'padding' && isMargin)
      ) {
        this.selected = [];
      }
      if (!removed) this.selected.push(side);
      if (isMargin) {
        this.lastSelected = 'margin';
      } else {
        this.lastSelected = 'padding';
      }
      if (isMargin) {
        this.marginIsSelected = true;
        this.negativeSelected =
          this.selected[0] &&
          this.getMarginValue(this.selected[0]) !== null &&
          this.marginOptions.indexOf(this.getMarginValue(this.selected[0])) <
            this.marginOptions.indexOf('0') &&
          this.marginOptions.indexOf(this.getMarginValue(this.selected[0])) >
            -1;
      } else {
        this.marginIsSelected = false;
        this.negativeSelected = false;
      }
    },
    sliderValue() {
      let prefix = 'padding';
      let options = this.paddingOptions;
      if (this.marginIsSelected) {
        prefix = 'margin';
        options = this.marginOptions;
      }
      const value = this.values[prefix + ucfirst(this.selected[0])];
      if (value === null) return 0;
      let index = options.indexOf(value);
      if (this.marginIsSelected && !this.negativeSelected)
        index -= this.marginOptions.indexOf('0');
      return index + 1;
    }
  }
};
</script>
